import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Buildings, Plus, Spinner, Copy, Trash, PauseCircle, PlayCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const PLAN_KEYS = ["basic", "essential", "pro", "enterprise"];
const PLAN_LABELS = { basic: "Basic", essential: "Essential", pro: "Pro", enterprise: "Enterprise" };

export default function AdminCompanies() {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ company_name: "", admin_name: "", admin_email: "", admin_password: "", plan: "enterprise" });
  const [created, setCreated] = useState(null);
  const [delTarget, setDelTarget] = useState(null);

  const load = () => { setLoading(true); api.get("/admin/companies").then(({ data }) => { setCompanies(data); setLoading(false); }).catch(() => setLoading(false)); };
  useEffect(load, []);

  const actAs = (c) => {
    localStorage.setItem("aim_active_company", c.id);
    localStorage.setItem("aim_active_company_name", c.name);
    window.location.href = "/app/dashboard";
  };

  if (user.role !== "super_admin") {
    return <div className="p-8 text-zinc-500" data-testid="admin-denied">Super admin access only.</div>;
  }

  const create = async () => {
    if (!form.company_name || !form.admin_name || !form.admin_email) { toast.error("Fill in company, admin name and email"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/companies", form);
      setCreated(data);
      toast.success("Company created");
      setForm({ company_name: "", admin_name: "", admin_email: "", admin_password: "", plan: "enterprise" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const setPlan = async (cid, plan) => {
    try {
      await api.put(`/admin/companies/${cid}/plan`, { plan });
      toast.success("Plan updated");
      setCompanies((cs) => cs.map((c) => (c.id === cid ? { ...c, plan } : c)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggleHold = async (c) => {
    const status = c.status === "hold" ? "active" : "hold";
    try {
      await api.put(`/admin/companies/${c.id}/status`, { status });
      toast.success(status === "hold" ? "Company placed on hold" : "Company reactivated");
      setCompanies((cs) => cs.map((x) => (x.id === c.id ? { ...x, status } : x)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const del = async () => {
    try {
      await api.delete(`/admin/companies/${delTarget.id}`);
      toast.success("Company deleted");
      setCompanies((cs) => cs.filter((x) => x.id !== delTarget.id));
      setDelTarget(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-companies-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">Companies</h1>
          <p className="text-zinc-500 text-sm mt-1">Create and manage tenant companies and their plans.</p>
        </div>
        <Button onClick={() => { setCreated(null); setOpen(true); }} className="h-11 gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold" data-testid="admin-add-company"><Plus size={18} weight="bold" /> New Company</Button>
      </div>

      <div className="rounded-lg border border-zinc-200 bg-white overflow-x-auto">
        {loading ? <div className="flex justify-center py-16"><Spinner size={28} className="animate-spin text-emerald-600" /></div> : (
          <Table>
            <TableHeader><TableRow className="bg-zinc-50"><TableHead>Company</TableHead><TableHead>Status</TableHead><TableHead>Admin email</TableHead><TableHead>Users</TableHead><TableHead>Assets</TableHead><TableHead>Plan</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {companies.map((c) => (
                <TableRow key={c.id} data-testid={`company-row-${c.id}`}>
                  <TableCell><button onClick={() => actAs(c)} className="font-semibold text-sm text-emerald-700 hover:underline text-left" data-testid={`company-open-${c.id}`}>{c.name}</button></TableCell>
                  <TableCell><Badge variant="outline" className={`rounded-md ${c.status === "hold" ? "border-amber-300 text-amber-700 bg-amber-50" : "border-emerald-300 text-emerald-700 bg-emerald-50"}`} data-testid={`company-status-${c.id}`}>{c.status === "hold" ? "On hold" : "Active"}</Badge></TableCell>
                  <TableCell className="text-sm text-zinc-600">{c.email || "—"}</TableCell>
                  <TableCell className="text-sm">{c.user_count}</TableCell>
                  <TableCell className="text-sm">{c.asset_count}</TableCell>
                  <TableCell>
                    <Select value={c.plan} onValueChange={(v) => setPlan(c.id, v)}>
                      <SelectTrigger className="h-9 w-32" data-testid={`company-plan-${c.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{PLAN_KEYS.map((p) => <SelectItem key={p} value={p}>{PLAN_LABELS[p]}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" onClick={() => toggleHold(c)} className={`h-8 gap-1.5 ${c.status === "hold" ? "text-emerald-700" : "text-amber-600"}`} data-testid={`company-hold-${c.id}`}>
                      {c.status === "hold" ? <><PlayCircle size={16} weight="bold" /> Reactivate</> : <><PauseCircle size={16} weight="bold" /> Hold</>}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDelTarget(c)} className="h-8 gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`company-delete-${c.id}`}>
                      <Trash size={16} /> Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-xl">New Company</DialogTitle></DialogHeader>
          {created ? (
            <div className="space-y-3 py-2" data-testid="admin-created-box">
              <p className="text-sm text-zinc-600">Company <strong>{created.name}</strong> created on the <strong>{PLAN_LABELS[created.plan]}</strong> plan. Share these login details with the client:</p>
              <div className="rounded-md bg-zinc-50 border border-zinc-200 p-3 text-sm space-y-1">
                <div className="flex items-center justify-between"><span className="text-zinc-500">Email</span><span className="font-mono">{created.admin_email}</span></div>
                <div className="flex items-center justify-between"><span className="text-zinc-500">Temp password</span><span className="font-mono">{created.temp_password}</span></div>
              </div>
              <Button variant="outline" className="w-full gap-2" onClick={() => { navigator.clipboard.writeText(`Email: ${created.admin_email}\nPassword: ${created.temp_password}`); toast.success("Copied"); }} data-testid="admin-copy-creds"><Copy size={16} /> Copy credentials</Button>
              <p className="text-xs text-zinc-400">Ask them to change the password after first sign-in.</p>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div><Label className="text-sm font-semibold">Company name</Label><Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="h-11 mt-1.5" data-testid="admin-field-company" /></div>
              <div><Label className="text-sm font-semibold">Admin name</Label><Input value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} className="h-11 mt-1.5" data-testid="admin-field-name" /></div>
              <div><Label className="text-sm font-semibold">Admin email</Label><Input type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} className="h-11 mt-1.5" data-testid="admin-field-email" /></div>
              <div><Label className="text-sm font-semibold">Temp password <span className="font-normal text-zinc-400">(optional — auto-generated if blank)</span></Label><Input value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} className="h-11 mt-1.5" data-testid="admin-field-password" /></div>
              <div><Label className="text-sm font-semibold">Plan</Label>
                <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                  <SelectTrigger className="h-11 mt-1.5" data-testid="admin-field-plan"><SelectValue /></SelectTrigger>
                  <SelectContent>{PLAN_KEYS.map((p) => <SelectItem key={p} value={p}>{PLAN_LABELS[p]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            {created ? (
              <Button onClick={() => setOpen(false)} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="admin-done">Done</Button>
            ) : (
              <Button onClick={create} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-white gap-2" data-testid="admin-create-submit">{saving && <Spinner size={16} className="animate-spin" />} Create Company</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-xl">Delete company?</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-600 py-2">This permanently deletes <strong>{delTarget?.name}</strong> and all of its data — users, assets, locations, movement history and files. This cannot be undone. Consider putting them <strong>On hold</strong> instead.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelTarget(null)} data-testid="admin-delete-cancel">Cancel</Button>
            <Button onClick={del} className="bg-red-600 hover:bg-red-700 text-white gap-2" data-testid="admin-delete-confirm"><Trash size={16} /> Delete permanently</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
